import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import FilterBar from './components/FilterBar';
import KPICard from './components/KPICard';
import ReportChart from './components/ReportChart';
import ReportAnalysis from './components/ReportAnalysis';
import ExportButton from './components/ExportButton';
import { getVehicleReports } from '../../../services/reportService';
import { CHART_COLORS } from './utils/chartConfig';
import { prepareKPIsForExport } from './utils/exportUtils';
import './Reports.css';

const VehicleReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [reportData, setReportData] = useState(null);

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getVehicleReports(filters);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching vehicle reports:', err);
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const vehicleStatusData = reportData?.vehicleStatus ? {
        labels: ['Actifs', 'Inactifs'],
        datasets: [{
            data: [reportData.vehicleStatus.active, reportData.vehicleStatus.inactive],
            backgroundColor: [CHART_COLORS.success, CHART_COLORS.light],
            borderWidth: 0
        }]
    } : null;

    const revenueByVehicleData = reportData?.revenueByVehicle ? {
        labels: reportData.revenueByVehicle.map(item => item.vehiclePlate),
        datasets: [{
            label: 'Chiffre d\'affaires',
            data: reportData.revenueByVehicle.map(item => item.revenue),
            backgroundColor: CHART_COLORS.primary,
            borderRadius: 8
        }]
    } : null;

    const exportData = reportData ? [
        ...prepareKPIsForExport(reportData.kpis),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'CA par Véhicule (Top 10)', Valeur: '' },
        ...(reportData.revenueByVehicle || []).map(item => ({ Indicateur: item.vehiclePlate, Valeur: item.revenue }))
    ] : null;

    return (
        <DashboardLayout>
            <div className="reports-page">
                <div className="reports-header">
                    <div>
                        <h1 className="reports-title">Rapports & Analytics</h1>
                        <p className="reports-subtitle">Rapports véhicules</p>
                    </div>
                    <ExportButton
                        reportName="rapports_vehicules"
                        data={exportData}
                        headers={['Indicateur', 'Valeur']}
                        keys={['Indicateur', 'Valeur']}
                        disabled={loading || !reportData}
                    />
                </div>

                <FilterBar
                    onFilterChange={setFilters}
                    availableFilters={['currency', 'period', 'vehicle', 'status']}
                />

                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Analysis Section */}
                {reportData?.analysis && (
                    <ReportAnalysis analysis={reportData.analysis} />
                )}

                <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Véhicules actifs"
                            value={reportData?.kpis?.activeVehicles}
                            format="number"
                            icon="bi bi-truck"
                            gradient="green"
                            loading={loading}
                        />
                    </div>

                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA total véhicules"
                            value={reportData?.kpis?.totalRevenue}
                            previousValue={reportData?.kpis?.previousTotalRevenue}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-currency-euro"
                            gradient="purple"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Taux d'utilisation"
                            value={reportData?.kpis?.utilizationRate}
                            format="percentage"
                            icon="bi bi-graph-up"
                            gradient="orange"
                            loading={loading}
                            decimals={1}
                        />
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-4">
                        <ReportChart
                            type="doughnut"
                            data={vehicleStatusData}
                            title="Statut des véhicules"
                            height={350}
                            loading={loading}
                            id="vehicle-status-chart"
                        />
                    </div>
                    <div className="col-lg-8">
                        <ReportChart
                            type="bar"
                            data={revenueByVehicleData}
                            title="Chiffre d'affaires par véhicule (Top 10)"
                            height={350}
                            loading={loading}
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default VehicleReports;
